import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { 
  Workstation, 
  WorkstationFilter, 
  WorkstationFormData,
  WorkstationOperation,
  WorkstationAttachment 
} from '@/types/workstations';
import { useToast } from './useToast';
import { UUID } from '@/types/common';

/**
 * Custom hook for workstation management operations
 * Provides functions for CRUD operations and workstation data retrieval
 */
export function useWorkstations() {
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Fetch all workstations with optional filtering
   */
  const fetchWorkstations = async (filters?: WorkstationFilter) => {
    setIsLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('workstations')
        .select(`
          *,
          departments:department_id(id, name),
          suppliers:supplier_id(id, name),
          workstation_operations(id, operations:operation_id(id, name)),
          workstation_attachments(*)
        `);

      // Apply filters if provided
      if (filters) {
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        if (filters.type) {
          query = query.eq('type', filters.type);
        }
        if (filters.department_id) {
          query = query.eq('department_id', filters.department_id);
        }
        if (filters.search) {
          query = query.ilike('name', `%${filters.search}%`);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform the data to match our interface
      const formattedData: Workstation[] = (data || []).map(item => ({
        ...item,
        department_name: item.departments?.name,
        supplier_name: item.suppliers?.name,
        operations: item.workstation_operations?.map((wo: any) => wo.operations),
        attachments: item.workstation_attachments,
        working_hours: item.working_hours || []
      }));

      setWorkstations(formattedData);
    } catch (err) {
      console.error('Error fetching workstations:', err);
      setError('Failed to load workstations');
      toast({
        title: 'Error',
        description: 'Failed to load workstations',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch a single workstation by ID
   */
  const getWorkstation = async (id: UUID): Promise<Workstation | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('workstations')
        .select(`
          *,
          departments:department_id(id, name),
          suppliers:supplier_id(id, name),
          workstation_operations(id, operations:operation_id(id, name)),
          workstation_attachments(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Transform the data to match our interface
      return {
        ...data,
        department_name: data.departments?.name,
        supplier_name: data.suppliers?.name,
        operations: data.workstation_operations?.map((wo: any) => wo.operations),
        attachments: data.workstation_attachments,
        working_hours: data.working_hours || []
      };
    } catch (err) {
      console.error(`Error fetching workstation ${id}:`, err);
      setError(`Failed to load workstation details`);
      toast({
        title: 'Error',
        description: `Failed to load workstation details`,
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create a new workstation
   */
  const createWorkstation = async (workstationData: WorkstationFormData, userId: UUID): Promise<UUID | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // Extract operations to handle separately
      const { operations, ...workstationFields } = workstationData;
      
      // Insert workstation record
      const { data, error } = await supabase
        .from('workstations')
        .insert({
          ...workstationFields,
          created_by: userId
        })
        .select('id')
        .single();

      if (error) throw error;
      
      const workstationId = data.id;

      // If operations provided, add them to the junction table
      if (operations && operations.length > 0) {
        const operationLinks = operations.map(operationId => ({
          workstation_id: workstationId,
          operation_id: operationId
        }));

        const { error: opError } = await supabase
          .from('workstation_operations')
          .insert(operationLinks);

        if (opError) {
          console.error('Error linking operations:', opError);
          // Continue despite error - we'll at least have the workstation
        }
      }

      toast({
        title: 'Success',
        description: 'Workstation created successfully',
      });

      // Refresh the workstations list
      await fetchWorkstations();
      
      return workstationId;
    } catch (err) {
      console.error('Error creating workstation:', err);
      setError('Failed to create workstation');
      toast({
        title: 'Error',
        description: 'Failed to create workstation',
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing workstation
   */
  const updateWorkstation = async (id: UUID, workstationData: WorkstationFormData): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Extract operations to handle separately
      const { operations, ...workstationFields } = workstationData;
      
      // Update workstation record
      const { error } = await supabase
        .from('workstations')
        .update(workstationFields)
        .eq('id', id);

      if (error) throw error;

      // If operations provided, handle the junction table
      if (operations !== undefined) {
        // First, remove all existing links
        const { error: delError } = await supabase
          .from('workstation_operations')
          .delete()
          .eq('workstation_id', id);

        if (delError) throw delError;

        // Then add new links
        if (operations.length > 0) {
          const operationLinks = operations.map(operationId => ({
            workstation_id: id,
            operation_id: operationId
          }));

          const { error: opError } = await supabase
            .from('workstation_operations')
            .insert(operationLinks);

          if (opError) throw opError;
        }
      }

      toast({
        title: 'Success',
        description: 'Workstation updated successfully',
      });

      // Refresh the workstations list
      await fetchWorkstations();
      
      return true;
    } catch (err) {
      console.error('Error updating workstation:', err);
      setError('Failed to update workstation');
      toast({
        title: 'Error',
        description: 'Failed to update workstation',
        variant: 'destructive'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a workstation
   */
  const deleteWorkstation = async (id: UUID): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('workstations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Workstation deleted successfully',
      });

      // Refresh the workstations list
      await fetchWorkstations();
      
      return true;
    } catch (err) {
      console.error('Error deleting workstation:', err);
      setError('Failed to delete workstation');
      toast({
        title: 'Error',
        description: 'Failed to delete workstation',
        variant: 'destructive'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update workstation status
   */
  const updateWorkstationStatus = async (id: UUID, status: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('workstations')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Workstation status updated to ${status}`,
      });

      // Refresh the workstations list
      await fetchWorkstations();
      
      return true;
    } catch (err) {
      console.error('Error updating workstation status:', err);
      setError('Failed to update workstation status');
      toast({
        title: 'Error',
        description: 'Failed to update workstation status',
        variant: 'destructive'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Upload and attach a file to a workstation
   */
  const uploadAttachment = async (
    workstationId: UUID, 
    file: File, 
    userId: UUID
  ): Promise<WorkstationAttachment | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Upload the file to storage
      const fileExt = file.name.split('.').pop();
      const filePath = `workstations/${workstationId}/${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('attachments')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 2. Get the public URL
      const { data: urlData } = await supabase.storage
        .from('attachments')
        .getPublicUrl(filePath);

      if (!urlData.publicUrl) throw new Error('Failed to get public URL');

      // 3. Create the attachment record
      const { data, error } = await supabase
        .from('workstation_attachments')
        .insert({
          workstation_id: workstationId,
          file_name: file.name,
          file_type: file.type,
          file_url: urlData.publicUrl,
          uploaded_by: userId
        })
        .select('*')
        .single();

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'File uploaded successfully',
      });

      return data;
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setError('Failed to upload attachment');
      toast({
        title: 'Error',
        description: 'Failed to upload attachment',
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete an attachment
   */
  const deleteAttachment = async (attachmentId: UUID): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Get the attachment to get the file path
      const { data: attachment, error: fetchError } = await supabase
        .from('workstation_attachments')
        .select('file_url')
        .eq('id', attachmentId)
        .single();

      if (fetchError) throw fetchError;

      // 2. Delete the attachment record
      const { error } = await supabase
        .from('workstation_attachments')
        .delete()
        .eq('id', attachmentId);

      if (error) throw error;

      // 3. Delete the file from storage if possible
      // This is a best-effort operation, we don't want to fail if the file doesn't exist
      try {
        const fileUrl = attachment.file_url;
        const filePath = fileUrl.split('/').slice(-2).join('/');
        await supabase.storage
          .from('attachments')
          .remove([filePath]);
      } catch (storageErr) {
        console.warn('Could not delete file from storage:', storageErr);
      }

      toast({
        title: 'Success',
        description: 'Attachment deleted successfully',
      });
      
      return true;
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment');
      toast({
        title: 'Error',
        description: 'Failed to delete attachment',
        variant: 'destructive'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkstations();
  }, []);

  return {
    workstations,
    isLoading,
    error,
    fetchWorkstations,
    getWorkstation,
    createWorkstation,
    updateWorkstation,
    deleteWorkstation,
    updateWorkstationStatus,
    uploadAttachment,
    deleteAttachment
  };
}
